import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, Users, Handshake, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminAgencies() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "create", editing: null });
  const [form, setForm] = useState({ name: "", contact_email: "", contact_phone: "", sso_email_domain: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agencies");
      setAgencies(data || []);
    } catch (e) { toast.error("Failed to load agencies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", contact_email: "", contact_phone: "", sso_email_domain: "" });
    setDialog({ open: true, mode: "create", editing: null });
  };
  const openEdit = (a) => {
    setForm({ name: a.name || "", contact_email: a.contact_email || "", contact_phone: a.contact_phone || "", sso_email_domain: a.sso_email_domain || "" });
    setDialog({ open: true, mode: "edit", editing: a });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_email: (form.contact_email || "").trim() || undefined,
        contact_phone: (form.contact_phone || "").trim() || undefined,
        sso_email_domain: (form.sso_email_domain || "").trim().toLowerCase().replace(/^@/, "") || undefined,
      };
      if (dialog.mode === "create") {
        await api.post("/admin/agencies", payload);
        toast.success("Agency created");
      } else {
        await api.put(`/admin/agencies/${dialog.editing.id}`, payload);
        toast.success("Agency updated");
      }
      setDialog({ open: false, mode: "create", editing: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (a.user_count > 0) {
      toast.error(`Cannot delete — ${a.user_count} user(s) still belong to this agency`);
      return;
    }
    if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/agencies/${a.id}`);
      toast.success("Agency deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Administration</div>
          <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] tracking-tight leading-none">Agencies</h1>
              <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">
                Every partner agency on the network. Create agencies, appoint the initial agency admin, and hand off day-to-day user management.
              </p>
            </div>
            <Button onClick={openCreate} className="bg-[#0B132B] hover:bg-[#0B132B]/90 h-10" data-testid="admin-add-agency-btn">
              <Plus size={15} className="mr-2" /> Add agency
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[#6C757D]">Loading…</div>
        ) : agencies.length === 0 ? (
          <Card className="p-10 border-2 border-dashed border-[#E9ECEF] rounded-sm text-center bg-white">
            <Building2 size={28} className="mx-auto text-[#6C757D]" />
            <div className="mt-2 text-sm text-[#6C757D]">No agencies yet. Add one above to get started.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agencies-grid">
            {agencies.map((a) => (
              <Card key={a.id} className="p-6 border border-[#E9ECEF] rounded-sm hover:border-[#0B132B]/40 transition-colors bg-white" data-testid={`agency-card-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-[#0B132B] truncate">{a.name}</div>
                    {a.contact_email && <div className="text-xs text-[#6C757D] truncate mt-0.5">{a.contact_email}</div>}
                    {a.contact_phone && <div className="text-xs text-[#6C757D]">{a.contact_phone}</div>}
                  </div>
                  {a.has_logo && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded-sm">Logo</span>}
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4 py-3 border-y border-[#E9ECEF]">
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Users</div>
                    <div className="font-display text-2xl font-black text-[#0B132B] mt-0.5">{a.user_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Dealers</div>
                    <div className="font-display text-2xl font-black text-[#0B132B] mt-0.5">{a.dealer_count || 0}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <Link to={`/admin/agencies/${a.id}/users`} className="flex-1">
                    <Button size="sm" className="w-full h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white text-xs" data-testid={`agency-manage-users-${a.id}`}>
                      <Users size={13} className="mr-1.5" /> Manage users <ArrowRight size={12} className="ml-1"/>
                    </Button>
                  </Link>
                  <Button size="icon" variant="outline" onClick={() => openEdit(a)} className="h-9 w-9 rounded-sm border-[#0B132B]/20" data-testid={`agency-edit-${a.id}`}>
                    <Pencil size={13}/>
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => remove(a)} className="h-9 w-9 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid={`agency-delete-${a.id}`}>
                    <Trash2 size={13}/>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, mode: "create", editing: null })}>
        <DialogContent className="sm:max-w-lg" data-testid="agency-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0B132B]">
              {dialog.mode === "edit" ? "Edit agency" : "Add agency"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6C757D]">
              {dialog.mode === "edit"
                ? "Update contact info. To rename, all associated users will auto-migrate."
                : "Create a new partner agency. Once created, add its first Agency Admin under Manage users."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-2">
              <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Agency name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="agency-form-name"/>
            </div>
            <div className="grid gap-2">
              <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Contact email</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} placeholder="Optional" data-testid="agency-form-email"/>
            </div>
            <div className="grid gap-2">
              <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Contact phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} placeholder="Optional" data-testid="agency-form-phone"/>
            </div>
            <div className="grid gap-2">
              <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">SSO email domain</Label>
              <Input value={form.sso_email_domain}
                onChange={(e) => setForm({...form, sso_email_domain: e.target.value})}
                placeholder="e.g. dealership.com — auto-assigns SSO users with @that-domain"
                data-testid="agency-form-sso-domain"/>
              <div className="text-[10px] text-[#6C757D]">Optional. Used by Admin → SSO to auto-place new SSO users into this agency.</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog({ open: false, mode: "create", editing: null })} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0B132B] hover:bg-[#0B132B]/90" data-testid="agency-form-submit">
                {saving ? "Saving…" : (dialog.mode === "edit" ? "Save changes" : "Create agency")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
